/**
 * @file: SamplingCheckManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import {
  CommonTaskStatus,
  DeliveryCheckSceneType,
  SamplingCheckResultType,
} from '@euler/model/enum';
import {
  CachedTaskDetail,
  SamplingCheck,
  TaskDetailVersions,
} from '@euler/model/task-detail';
import { ITaskManager, TaskFileUploadRequestContext } from '../types';
import { DeliveryCheckManagerBase } from './DeliveryCheckManagerBase';

export class SamplingCheckManager extends DeliveryCheckManagerBase<SamplingCheck> {
  constructor(taskManager: ITaskManager) {
    super(taskManager, DeliveryCheckSceneType.SamplingCheck);
  }

  async save(options?: { saveOnly?: boolean }) {
    const payload = this.getCommitPayload(options?.saveOnly);

    payload.saveOnly = options?.saveOnly;

    const resp = await this.taskService.saveSamplingChecks(
      this.taskNo,
      payload,
    );

    this.handleVersionedResponse(resp, (_, { deliveryCheck, response }) => {
      Object.assign(deliveryCheck, response);
    });

    await this.discardCache();

    this.withChangeObserverTemporarilyDisabled(() => {
      this.resetWithDeliveryCheck(resp.response);
    });
  }

  async finish(options: {
    remark?: string;
    signatureImgUrl?: string;
    resultType: SamplingCheckResultType;
  }) {
    const resp = await this.taskService.finishDeliveryChecks(this.taskNo, {
      sceneType: DeliveryCheckSceneType.SamplingCheck,
      ...options,
    });
    this.handleVersionedResponse(resp, (_, { deliveryCheck }) => {
      deliveryCheck.status = CommonTaskStatus.Finished;
      deliveryCheck.finishedAt = new Date();
      deliveryCheck.finishedBy = this.currentUser.name;
      deliveryCheck.resultType = options.resultType;
    });
    await this.discardCache();
  }

  async requestAmend() {
    const resp = await this.taskService.requestUpdateDeliveryChecks(
      this.taskNo,
      DeliveryCheckSceneType.SamplingCheck,
    );
    this.handleVersionedResponse(resp, (_, { deliveryCheck }) => {
      deliveryCheck.status = CommonTaskStatus.InProgress;
    });
  }

  protected getDeliveryCheck(detail: CachedTaskDetail): SamplingCheck {
    return detail.samplingCheck;
  }

  protected getDeliveryCheckVersion(versions: TaskDetailVersions): number {
    return versions.samplingCheck;
  }

  protected getUploadEventContextType(): Extract<
    TaskFileUploadRequestContext['type'],
    'delivery-check' | 'sampling-check'
  > {
    return 'sampling-check';
  }
}
